'use client'

/**
 * Button — NoctrHost Design System (Part 32 §32.2)
 *
 * State wajib (Part 32 §32.1):
 *   ✓ default    ✓ hover      ✓ active/pressed
 *   ✓ focus-visible  ✓ disabled   ✓ loading
 *   ✓ aria-disabled (untuk tombol yang tampak disabled tapi masih focusable)
 *
 * ZERO raw hex — semua pakai token Tailwind dari tailwind.config.ts.
 */

import { forwardRef } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

// ── Variant definitions ──────────────────────────────────────────────────────
const buttonVariants = cva(
  // Base — shared across all variants
  [
    'inline-flex items-center justify-center gap-2 rounded-md',
    'font-sans font-semibold text-body-sm',
    'transition-colors duration-fast ease-out',
    'select-none',
    // Focus ring (WCAG 2.1 AA — Part 32 §32.6)
    'focus-visible:outline-none',
    'focus-visible:ring-2 focus-visible:ring-violet-500',
    'focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base',
    // Disabled state
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
    'aria-disabled:opacity-50 aria-disabled:cursor-not-allowed',
    // Min touch target 44px (Part 32 §32.1)
    'min-h-[44px] min-w-[44px]',
  ].join(' '),
  {
    variants: {
      variant: {
        // Primary — violet-700, white text
        primary: [
          'bg-violet-700 text-white',
          'hover:bg-violet-600',
          'active:bg-violet-800',
        ].join(' '),

        // Secondary — surface-2, text-primary
        secondary: [
          'bg-bg-s2 text-text-primary border border-border',
          'hover:bg-bg-s3',
          'active:border-border-strong',
        ].join(' '),

        // Ghost — transparent, text-muted
        ghost: [
          'bg-transparent text-text-muted',
          'hover:bg-bg-s2 hover:text-text-primary',
          'active:bg-bg-s3',
        ].join(' '),

        // Danger — red, white text
        danger: [
          'bg-danger text-white',
          'hover:bg-danger-hover',
          'active:bg-danger-active',
        ].join(' '),

        // Success — emerald-600, white text
        success: [
          'bg-emerald-600 text-white',
          'hover:bg-emerald-500',
          'active:bg-emerald-700',
        ].join(' '),
      },

      size: {
        sm: 'h-8 px-3 text-caption rounded-sm',
        md: 'h-10 px-4 py-2',
        lg: 'h-12 px-6 text-body',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

// ── Types ────────────────────────────────────────────────────────────────────
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
  /** Loading label for screen readers (default: "Memuat…") */
  loadingLabel?: string
  /** Render as a child element (e.g. Next.js Link) */
  asChild?: boolean
}

// ── Component ────────────────────────────────────────────────────────────────
import { Slot } from '@radix-ui/react-slot'

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading = false,
      loadingLabel = 'Memuat…',
      disabled,
      asChild = false,
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading
    const Comp = asChild ? Slot : "button"

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-label={loading ? loadingLabel : undefined}
        {...props}
      >
        {loading && !asChild ? (
          <>
            {/* Spinner — animasi hanya transform/opacity (Part 31 §31.9) */}
            <Loader2
              className="h-4 w-4 animate-spin"
              aria-hidden="true"
            />
            {/* Teks tetap, lebar tidak berubah (Part 32 §32.2) */}
            <span aria-hidden="true">{children}</span>
            <span className="sr-only">{loadingLabel}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    )
  },
)

Button.displayName = 'Button'

export { Button, buttonVariants }
